/**
 * Hook TanStack Query per il dominio Triage/VEX.
 * Pattern identico a features/products/hooks.ts (slice 1).
 */

import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  fetchTriage,
  updateAnalysis,
  fetchVex,
  fetchTicketCounts,
  fetchTickets,
  fetchTicket,
  updateTicketNotes,
  promoteTicket,
  startTicket,
  leaveTicket,
  addTicketCollaborator,
  removeTicketCollaborator,
  closeTicket,
  fetchCustomerNoteSettings,
  reopenTicket,
  updateTicketCveState,
  createTicket,
  assignTicketCve,
  unassignTicketCve,
  searchKnownCves,
} from './api.js';
import { customerKeys } from '../customers/hooks.js';
import { CVE_SEARCH_MIN_QUERY } from './types.js';
import type { ReportCategory } from '../srp/category.js';
import type {
  AnalysisResult,
  CloseTicketInput,
  CreateTicketInput,
  CustomerNoteSettings,
  KnownCve,
  KnownCveSearchResult,
  Page,
  Ticket,
  TicketStatus,
  PromoteTicketResult,
  TriageItem,
  UpdateAnalysisInput,
} from './types.js';

/** Chiavi query centralizzate per il dominio triage. */
export const triageKeys = {
  all: ['triage'] as const,
  lista: (projectUuid: string) => ['triage', 'lista', projectUuid] as const,
  counts: ['triage', 'product', 'counts'] as const,
  tickets: (status: TicketStatus) => ['triage', 'product', status] as const,
  ticket: (id: number) => ['triage', 'product', 'dettaglio', id] as const,
  cveSearch: (q: string, limit: number) => ['triage', 'cve-search', q, limit] as const,
  customerNoteSettings: ['triage', 'customer-note-settings'] as const,
} as const;

/**
 * Parole minime della nota per il cliente (impostazione /admin), lette dal dialog di chiusura.
 * @param abilitato - false se il ticket non richiede la nota (automatico).
 */
export function useCustomerNoteSettings(abilitato = true): UseQueryResult<CustomerNoteSettings> {
  return useQuery({
    queryKey: triageKeys.customerNoteSettings,
    queryFn: fetchCustomerNoteSettings,
    enabled: abilitato,
    staleTime: 30_000,
  });
}

/**
 * Recupera la lista triage dei finding per una release, già ordinata dal server
 * (US-2.7.2/US-2.8.1: knownExploited → no-VEX → cvssScore desc). Abilitato solo se projectUuid è
 * non vuoto.
 */
export function useTriage(projectUuid: string): UseQueryResult<Page<TriageItem>> {
  return useQuery({
    queryKey: triageKeys.lista(projectUuid),
    queryFn: () => fetchTriage(projectUuid),
    enabled: projectUuid.length > 0,
  });
}

/**
 * Mutation per aggiornare l'analisi di un finding.
 * Invalida la lista triage della release dopo il successo.
 * Può rigettare con ApiError 422 se lo stato o la giustificazione non sono valori CycloneDX.
 */
export function useUpdateAnalysis(
  projectUuid: string,
): UseMutationResult<AnalysisResult, Error, UpdateAnalysisInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => updateAnalysis(projectUuid, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: triageKeys.lista(projectUuid) });
    },
  });
}

/**
 * Mutation per scaricare il documento VEX CycloneDX.
 * Non invalida query perché è un'operazione di sola lettura (export).
 * Il chiamante usa il risultato per creare un Blob e offrire il download.
 */
export function useVexExport(projectUuid: string): UseMutationResult<unknown, Error, void> {
  return useMutation({
    mutationFn: () => fetchVex(projectUuid),
  });
}

// ---------------------------------------------------------------------------
// Ticket (rework auto-creazione SRP) — vista #by-product
// ---------------------------------------------------------------------------

/** Contatori sidebar: { open, inTriage }. Polling ogni 60s per tenerli aggiornati. */
export function useTicketCounts(): UseQueryResult<{ open: number; inTriage: number }> {
  return useQuery({
    queryKey: triageKeys.counts,
    queryFn: fetchTicketCounts,
    refetchInterval: 60_000,
  });
}

/**
 * Lista dei Ticket filtrata per stato (default: open).
 * `enabled: false` sospende la fetch (es. i chiusi, caricati solo se mostrati).
 */
export function useTickets(
  status: TicketStatus = 'open',
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<Ticket[]> {
  return useQuery({
    queryKey: triageKeys.tickets(status),
    queryFn: () => fetchTickets(status),
    enabled,
  });
}

/** Dettaglio di un singolo Ticket. Abilitato solo se `id` è valorizzato. */
export function useTicket(id: number | undefined): UseQueryResult<Ticket> {
  return useQuery({
    queryKey: triageKeys.ticket(id ?? -1),
    queryFn: () => fetchTicket(id as number),
    enabled: id != null && id > 0,
  });
}

/** Mutation per aggiornare le note analista; invalida lista + dettaglio. */
export function useUpdateTicketNotes(
  id: number,
): UseMutationResult<Ticket, Error, { analystNotes: string; expectedNotes?: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ analystNotes, expectedNotes }) =>
      updateTicketNotes(id, analystNotes, expectedNotes),
    // Anche in caso di conflitto (409) ricarica il dettaglio: serve la versione salvata da altri.
    onSettled: () => invalidaTriage(queryClient, id),
  });
}

/**
 * Mutation per promuovere un Ticket a bozza SRP.
 * Al successo il triage esce dalle liste attive → invalida le liste e il dettaglio.
 */
export function usePromoteTicket(
  id: number,
): UseMutationResult<PromoteTicketResult, Error, ReportCategory> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (category) => promoteTicket(id, category),
    onSuccess: () => invalidaTriage(queryClient, id),
  });
}

/**
 * Invalida tutte le liste di Ticket (per stato) + il dettaglio indicato.
 * Usata da tutte le mutation di workflow: lo stato cambia e il triage migra tra le sezioni.
 */
function invalidaTriage(queryClient: ReturnType<typeof useQueryClient>, id: number): void {
  for (const status of ['open', 'in_triage', 'promoted', 'closed'] as const) {
    void queryClient.invalidateQueries({ queryKey: triageKeys.tickets(status) });
  }
  void queryClient.invalidateQueries({ queryKey: triageKeys.ticket(id) });
}

/** Presa in carico (open → in_triage): l'utente diventa il triage lead. */
export function useStartTicket(id: number): UseMutationResult<Ticket, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => startTicket(id),
    onSuccess: () => invalidaTriage(queryClient, id),
  });
}

/** Abbandono (in_triage → open, solo lead). Notifica il system admin lato server. */
export function useLeaveTicket(id: number): UseMutationResult<Ticket, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => leaveTicket(id),
    onSuccess: () => invalidaTriage(queryClient, id),
  });
}

/** Aggiunge un collaboratore (solo lead). */
export function useAddCollaborator(id: number): UseMutationResult<Ticket, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (person) => addTicketCollaborator(id, person),
    onSuccess: () => invalidaTriage(queryClient, id),
  });
}

/** Rimuove un collaboratore (solo lead). */
export function useRemoveCollaborator(id: number): UseMutationResult<Ticket, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (person) => removeTicketCollaborator(id, person),
    onSuccess: () => invalidaTriage(queryClient, id),
  });
}

/** Chiusura anticipata (in_triage → closed, lead/collaboratore). */
export function useCloseTicket(id: number): UseMutationResult<Ticket, Error, CloseTicketInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => closeTicket(id, input),
    onSuccess: () => invalidaTriage(queryClient, id),
  });
}

/** Riapertura (closed → open) di un ticket chiuso non promosso; motivo facoltativo. */
export function useReopenTicket(id: number): UseMutationResult<Ticket, Error, string | undefined> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason) => reopenTicket(id, reason),
    onSuccess: () => {
      invalidaTriage(queryClient, id);
      void queryClient.invalidateQueries({ queryKey: triageKeys.counts });
    },
  });
}

/** Aggiorna lo stato VEX di una CVE del triage (lead/collaboratore). */
export function useUpdateCveState(
  id: number,
): UseMutationResult<Ticket, Error, { cveId: string; analysisState: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cveId, analysisState }) => updateTicketCveState(id, cveId, analysisState),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: triageKeys.ticket(id) });
      void queryClient.invalidateQueries({ queryKey: triageKeys.tickets('in_triage') });
    },
  });
}

/** Crea un Ticket manuale; al successo invalida le liste (nasce in "da avviare"). */
export function useCreateTicket(): UseMutationResult<Ticket, Error, CreateTicketInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTicket,
    onSuccess: (ticket, input) => {
      invalidaTriage(queryClient, ticket.id);
      // Un segnalante nuovo viene registrato tra i contatti del cliente: la lista va riletta.
      const rep = input.source?.reporter;
      if (rep?.customerId !== undefined && rep.email) {
        void queryClient.invalidateQueries({ queryKey: customerKeys.contacts(rep.customerId) });
      }
      void queryClient.invalidateQueries({ queryKey: triageKeys.counts });
    },
  });
}

/** Assegna una CVE nota a un Ticket manuale; invalida dettaglio + liste. */
export function useAssignTicketCve(id: number): UseMutationResult<Ticket, Error, KnownCve> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cve) => assignTicketCve(id, cve),
    onSuccess: () => invalidaTriage(queryClient, id),
  });
}

/** Rimuove una CVE da un Ticket manuale; invalida dettaglio + liste. */
export function useUnassignTicketCve(id: number): UseMutationResult<Ticket, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cveId) => unassignTicketCve(id, cveId),
    onSuccess: () => invalidaTriage(queryClient, id),
  });
}

/** Valore che si aggiorna solo dopo `delayMs` di quiete (debounce per il typeahead). */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Typeahead sulle CVE note: la query parte solo con ≥ CVE_SEARCH_MIN_QUERY caratteri, dopo
 * 300 ms di quiete; le richieste superate vengono annullate (AbortSignal), i risultati restano
 * in cache 60 s e quelli precedenti restano visibili mentre arriva la nuova pagina. Il server
 * restituisce al più `limit` voci: il catalogo non viene mai scaricato.
 */
export function useKnownCveSearch(
  query: string,
  limit = 10,
): UseQueryResult<KnownCveSearchResult> & { debouncedQuery: string } {
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const result = useQuery({
    queryKey: triageKeys.cveSearch(debouncedQuery.toUpperCase(), limit),
    queryFn: ({ signal }) => searchKnownCves(debouncedQuery, limit, signal),
    enabled: debouncedQuery.length >= CVE_SEARCH_MIN_QUERY,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
  return Object.assign(result, { debouncedQuery });
}
